import {
  NumberKeyframeTrack,
  VectorKeyframeTrack,
  BooleanKeyframeTrack,
  ColorKeyframeTrack,
  QuaternionKeyframeTrack,
  StringKeyframeTrack,
  FileLoader,
  TextureLoader,
  Mesh,
  Vector3,
  Box3Helper,
  BufferAttribute,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshDepthMaterial,
  MeshNormalMaterial,
  MeshPhongMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  AnimationClip,
  CubeTextureLoader,
  NearestFilter,
  ShaderMaterial,
  Vector2,
  Group,
  BufferGeometry,
  ColorManagement,
  SRGBColorSpace,
} from 'three'

import { Game } from '../Core'
import { qrToWorld } from './coords'
import Sprite2D from './Sprite2D'
import SimpleModel from './SimpleModel'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'

import * as MarsUtils from '../Mars/Util/util'

const fileLoader = new FileLoader()
const textureLoader = new TextureLoader()
const cubeTextureLoader = new CubeTextureLoader()
// DRACOLoader setup moved to loadModel function where it's used

// Enable Three.js color management for proper texture handling
// This ensures GLTFLoader correctly sets colorSpace for textures
ColorManagement.enabled = true

export function sortGeometryByIndex(model) {
  model.traverse((node) => {
    if (node.isMesh) {
      // let newGeo = sortFacesByMaterial(node.geometry)
      // node.geometry = newGeo
    }
  })
}

export function sortFacesByMaterial(nodeGeo) {
  // DISABLED: Geometry class was removed in modern Three.js
  // TODO: Reimplement using BufferGeometry if needed
  console.warn('sortFacesByMaterial disabled - requires Three.js BufferGeometry migration')
  return nodeGeo
  
  /* LEGACY CODE - DISABLED
  let geometry = new Geometry().fromBufferGeometry(nodeGeo)
  let perMaterial = {}

  //Categorize faces and uvs by materialIndex
  for (let i = 0; i < geometry.faces.length; i++) {
    let f = geometry.faces[i]
    if (!perMaterial[f.materialIndex]) {
      perMaterial[f.materialIndex] = []
    }
    let temp = {}
    temp.face = f
    temp.uvs = []
    for (let j = 0; j < geometry.faceVertexUvs.length; j++) {
      temp.uvs.push(geometry.faceVertexUvs[j][i])
    }

    perMaterial[f.materialIndex].push(temp)
  }

  //Create new ordered arrays
  let faces = []
  let uvs = []

  for (let key in perMaterial) {
    for (let i = 0; i < perMaterial[key].length; i++) {
      let temp = perMaterial[key][i]
      faces.push(temp.face)
      for (let j = 0; j < temp.uvs.length; j++) {
        if (!uvs[j]) uvs[j] = []
        uvs[j].push(temp.uvs[j])
      }
    }
  }

  //Asign new arrays to geometry
  geometry.faces = faces

  for (let i = 0; i < uvs.length; i++) {
    geometry.faceVertexUvs[i] = uvs[i]
  }

  geometry.elementsNeedUpdate = true

  return new BufferGeometry().fromGeometry(geometry)
  */ // END LEGACY CODE - DISABLED
}

export function loadTexture(url, onProgress) {
  return new Promise((resolve, reject) => {
    const onLoad = (texture) => {
      // Three.js v0.180.0: Set colorSpace for manually loaded color textures
      // PNG/JPEG textures containing color data need sRGB colorSpace
      texture.colorSpace = SRGBColorSpace
      texture.needsUpdate = true
      resolve(texture)
    }
    const onProgressWrapper = (progress) => {
      if (onProgress) {
        onProgress(100 * (progress.loaded / progress.total), progress.total, progress.loaded)
      }
    }
    const onError = (error) => {
      reject(error)
    }
    textureLoader.load(url, onLoad, onProgressWrapper, onError)
  })
}

export function loadModel(loader, url, onProgress) {
  if (!loader) {
    return null
  }
  return new Promise((resolve, reject) => {
    const onLoad = (gltf) => {
      // Three.js v0.180.0: Ensure all color textures have correct colorSpace
      // GLTFLoader should handle this automatically, but we verify/fix it here
      if (gltf.scene) {
        gltf.scene.traverse((node) => {
          if (node.isMesh) {
            // Handle both single materials and material arrays
            const materials = Array.isArray(node.material) ? node.material : [node.material]

            materials.forEach((material) => {
              if (!material) return

              // List of material properties that contain color textures
              // These textures should use sRGB color space
              const colorTextureProps = ['map', 'emissiveMap', 'specularMap']

              // List of non-color textures that should remain linear
              // const linearTextureProps = ['normalMap', 'roughnessMap', 'metalnessMap', 'displacementMap', 'aoMap', 'lightMap']

              colorTextureProps.forEach((prop) => {
                if (material[prop] && material[prop].isTexture) {
                  // Set colorSpace to sRGB for color textures
                  console.log(`Setting colorSpace for ${prop}:`, material[prop].colorSpace, '-> SRGBColorSpace')
                  material[prop].colorSpace = SRGBColorSpace
                  material[prop].needsUpdate = true
                }
              })

              material.needsUpdate = true
            })
          }
        })
      }

      resolve(gltf)
    }
    const onProgressWrapper = (progress) => {
      if (onProgress) {
        onProgress(100 * (progress.loaded / progress.total), progress.total, progress.loaded)
      }
    }
    const onError = (error) => {
      reject(error)
    }
    // Setup DRACOLoader correctly for modern Three.js
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('/assets/vendor/draco/')
    loader.setDRACOLoader(dracoLoader)

    loader.load(url, onLoad, onProgressWrapper, onError)
  })
}

export function combineBuffer(model, bufferName) {
  let count = 0
  let offset = 0
  model.traverse(function (child) {
    if (child.isMesh) {
      var buffer = child.geometry.attributes[bufferName]
      count += buffer.array.length
    }
  })
  var combined = new Float32Array(count)
  model.traverse(function (child) {
    if (child.isMesh) {
      var buffer = child.geometry.attributes[bufferName]
      combined.set(buffer.array, offset)
      offset += buffer.array.length
    }
  })
  return new BufferAttribute(combined, 3)
}

export function handleCustomParams(b_modifications, type, callback) {
  Object.values(b_modifications).map((modification) => {
    switch (modification.type) {
      case type: {
        callback(modification)
        break
      }
    }
  })
}

export function assignMeshParameters(
  store,
  object,
  parametersModifications,
  meshModifications,
  simpleModel = false
) {
  if (!parametersModifications || !meshModifications || !object) {
    return null
  } else {
    handleMeshMods(store, object, parametersModifications.meshes, meshModifications, simpleModel)
  }
}

export function applyShadowOnModel(model) {
  if (model.hasOwnProperty('receiveShadow')) {
    model.receiveShadow = true
    model.castShadow = true
  }
  if (model.children && model.children.length) {
    for (var i = 0; i < model.children.length; i++) {
      applyShadowOnModel(model.children[i])
    }
  }
}

export function fillMap(map, buildAsset, modelFactory = null) {
  if (!map.data.data || !buildAsset) {
    return null
  }

  let group = new Group()

  Object.values(map.data.data).map((tile) => {
    let tilePos = qrToWorld(tile.q, tile.r)
    let modelAsset = MarsUtils.asset(buildAsset)
    let newModel = new SimpleModel(modelAsset, modelFactory)
    newModel.position.set(tilePos.x, tilePos.y, tilePos.z)

    group.add(newModel)
  })

  return group
}

export function centerPivotByBoundingBox(mesh) {
  var boundingBox = mesh.geometry.boundingBox

  if (boundingBox) {
    let position = new Vector3()
    position.subVectors(boundingBox.max, boundingBox.min)
    position.multiplyScalar(0.5)
    position.add(boundingBox.min)
    position.applyMatrix4(mesh.matrixWorld)

    mesh.geometry.center()
    // BufferGeometry automatically updates when attributes are modified
    mesh.geometry.computeBoundingBox()
    mesh.geometry.computeBoundingSphere()
    mesh.position.copy(position)
  } else {
    console.log('Bounding box needs to compute')
  }
}

export function deepClone(object) {
  let clone = object.clone()
  clone.traverse((node) => {
    if (node.isMesh) {
      node.material = node.material.clone()
      node.geometry = node.geometry.clone()
    }
  })
  return clone
}

export function animationClip(animation, objectName) {
  if (animation) {
    let frames = []
    animation.keyframes.map((keyframe) => {
      let frame = getKeyframeType(keyframe.type)
      frames.push(new frame(keyframe.command, keyframe.times, [].concat.apply([], keyframe.values)))
    })
    return new AnimationClip(animation.id + '_' + objectName, animation.duration, frames)
  }
}

export function modifiyMeshParams(usingMesh, params) {
  Object.keys(params).map((attribute) => {
    if (usingMesh) {
      if (usingMesh[attribute] instanceof Function) {
        let args = params[attribute]
        usingMesh[attribute](...args)
      } else {
        let meshAttribute = params[attribute]
        Object.keys(meshAttribute).map((attr) => {
          let insideAttribute = meshAttribute[attr]
          if (usingMesh[attribute][attr] instanceof Function) {
            let args = insideAttribute || []
            usingMesh[attribute][attr](...args)
          } else if (insideAttribute != undefined) {
            switch (attribute) {
              case 'material': {
                if (attr == 'modifyWith') {
                  let material = getNewMaterial(insideAttribute.type)
                  if (!material) {
                    return null
                  }
                  usingMesh.material = new material(insideAttribute.parameters)
                } else if (attr == 'color') {
                  usingMesh[attribute][attr].setRGB(...insideAttribute)
                } else if (attr.endsWith('Map') || attr == 'map') {
                  if (insideAttribute == false) {
                    usingMesh[attribute][attr] = null
                  } else {
                    let texture = null

                    if (insideAttribute.length == 6 && usingMesh.material.isMeshStandardMaterial) {
                      let loadCube = (name) =>
                        cubeTextureLoader.setPath(MarsUtils.asset('')).load(name)
                      texture = loadCube(insideAttribute)
                      texture.minFilter = NearestFilter
                    } else if (attr != 'envMap') {
                      let asset = MarsUtils.asset(insideAttribute[0])
                      texture = textureLoader.load(asset, (loadedTexture) => {
                        // Three.js v0.180.0: Set colorSpace for color textures
                        loadedTexture.colorSpace = SRGBColorSpace
                        loadedTexture.needsUpdate = true
                      })
                      // Also set it immediately for the reference
                      texture.colorSpace = SRGBColorSpace
                    }

                    usingMesh[attribute][attr] = texture
                  }
                } else {
                  usingMesh[attribute][attr] = insideAttribute
                }
                break
              }
            }
          }
        })
        if (usingMesh[attribute] instanceof Vector3 || usingMesh[attribute] instanceof Vector2) {
          if (attribute == 'position' && meshAttribute.length == 2) {
            let qrpos = qrToWorld(meshAttribute[0], meshAttribute[1])
            usingMesh[attribute].set(qrpos.x, qrpos.y, qrpos.z)
          }
          if (meshAttribute.length == 2) {
            usingMesh[attribute].set(...meshAttribute)
          }
          if (meshAttribute.length == 3) {
            usingMesh[attribute].set(...meshAttribute)
          }
        }

        if (attribute == 'centerPivots' && meshAttribute) {
          centerPivotByBoundingBox(usingMesh)
        }
        if (attribute == 'showBounding' && meshAttribute) {
          let box = usingMesh.geometry.boundingBox
          if (box) {
            var helper = new Box3Helper(box, 0xffff00)
            usingMesh.add(helper)
          } else {
            console.log('Bounding box needs to compute')
          }
        }
      }
    }
  })
}

export function hasAnyRunningAnimation(store, objectAnimations, allAnimations) {
  let result = {}
  if (store) {
    objectAnimations.map((animation) => {
      let currentAnimaton = allAnimations[animation]
      if (currentAnimaton) {
        result[animation] = currentAnimaton.conditions
          ? Game.evaluateAll(currentAnimaton.conditions, store)
          : !currentAnimaton.conditions
      }
    })
  }

  return Object.values(result).includes(true) || false
}

export function handleMeshMods(store, object, meshes, modifications, simpleModel = false) {
  let prefabs = meshes.preFabs
  let custom = meshes.custom

  if (simpleModel) {
    prefabs = meshes.customModelPrefabs || meshes.preFabs
  }

  if (Object.keys(custom).length > 0) {
    modifyMeshOf(store, object, custom)
  }

  prefabs.map((prefab) => {
    let modification = modifications[prefab]
    modifyMeshOf(store, object, modification)
  })
}

export function handleSprite(
  id,
  asset,
  rotation,
  position,
  scale,
  animations,
  multiple = null,
  material = {}
) {
  let newPosition = null
  let newRotation = null

  if (position && position.length == 3) {
    newPosition = new Vector3(position[0], position[1], position[2])
  } else if (position && position.length == 2) {
    newPosition = qrToWorld(position[0], position[1])
  } else {
    return null
  }

  if (rotation && rotation.length == 1) {
    newRotation = [rotation[0], rotation[0], rotation[0]]
  } else if (rotation && rotation.length == 3) {
    newRotation = [rotation[0], rotation[1], rotation[2]]
  }

  let sprite = new Sprite2D(id, asset, newRotation, newPosition.clone(), scale, multiple, material)
  sprite.setAnimations = animations

  return sprite
}

export function modifyMeshOf(store, object, modification) {
  let objectMeshes = getModelsOfType(object, Mesh)
  let objectMeshesIndexes = []
  objectMeshes.map((mesh, index) => objectMeshesIndexes.push(index))

  if (Object.keys(modification).length > 0 && objectMeshes.length > 0) {
    let indexes = modification.meshIndexes
    let canDisplay =
      modification.conditions && store
        ? Game.evaluateAll(modification.conditions, store)
        : !modification.conditions
    if (indexes[0] < 0) {
      indexes = objectMeshesIndexes
    }

    if (canDisplay || !modification.conditions) {
      indexes.map((index) => {
        let currentMesh = objectMeshes[index]
        modifiyMeshParams(currentMesh, modification.parameters)
      })
    }
  }
}
export function varying(...values) {
  return values[Math.round(Math.random() * (values.length - 1))]
}

export function getModelsOfType(model, type) {
  let meshes = []
  model.traverse((node) => {
    if (node instanceof type) {
      meshes.push(node)
    }
  })

  return meshes
}

export function getKeyframeType(type) {
  let object = null
  let loType = type.toLowerCase()
  switch (loType) {
    case 'number': {
      object = NumberKeyframeTrack
      break
    }
    case 'vector': {
      object = VectorKeyframeTrack
      break
    }
    case 'boolean': {
      object = BooleanKeyframeTrack
      break
    }
    case 'color': {
      object = ColorKeyframeTrack
      break
    }
    case 'quaternion': {
      object = QuaternionKeyframeTrack
      break
    }
    case 'string': {
      object = StringKeyframeTrack
      break
    }
  }

  return object
}

export function getNewMaterial(type) {
  let object = null
  switch (type.toLowerCase()) {
    case 'basic': {
      object = MeshBasicMaterial
      break
    }
    case 'lambert': {
      object = MeshLambertMaterial
      break
    }
    case 'depth': {
      object = MeshDepthMaterial
      break
    }
    case 'normal': {
      object = MeshNormalMaterial
      break
    }
    case 'phong': {
      object = MeshPhongMaterial
      break
    }
    case 'physical': {
      object = MeshPhysicalMaterial
      break
    }
    case 'standard': {
      object = MeshStandardMaterial
      break
    }
    case 'shader': {
      object = ShaderMaterial
      break
    }
  }

  return object
}

export function loadFile(path) {
  // TODO: Remove cache buster
  const url = path // + "?cachebuster=" + Math.random() * 9999999
  return new Promise((resolve, reject) => {
    fileLoader.load(
      url,
      (result) => {
        resolve(result)
      },
      undefined,
      (error) => {
        reject(error)
      }
    )
  })
}
export async function loadJSON(path) {
  return loadFile(path).then((str) => JSON.parse(str))
}
export function qrRange(qrRadius) {
  const coords = []
  forEachRange(-qrRadius, qrRadius + 1, (dx) => {
    forEachRange(
      Math.max(-qrRadius, -dx - qrRadius),
      Math.min(qrRadius, -dx + qrRadius) + 1,
      (dy) => {
        var dz = -dx - dy
        coords.push({
          q: dx,
          r: dz,
        })
      }
    )
  })
  return coords
}
export function forEachRange(min, max, f) {
  if (!max) {
    return range(0, min)
  } else {
    for (var i = min; i < max; i++) {
      f(i)
    }
  }
}
export function shuffle(a) {
  var j, x, i
  for (i = a.length; i; i--) {
    j = Math.floor(Math.random() * i)
    x = a[i - 1]
    a[i - 1] = a[j]
    a[j] = x
  }
  return a
}
export function range(minOrMax, max) {
  if (!max) {
    return this.range(0, minOrMax)
  } else {
    var values = []
    for (var i = minOrMax; i < max; i++) {
      values.push(i)
    }
    return values
  }
}
export function flatMap(items, map) {
  return [].concat.apply([], items.map(map))
}
export function sum(numbers) {
  return numbers.reduce((sum, item) => sum + item, 0)
}
export function qrEquals(a, b) {
  return a.q == b.q && a.r == b.r
}
export function minBy(items, by) {
  if (items.length == 0) {
    return null
  } else if (items.length == 1) {
    return items[0]
  } else {
    return items.reduce((min, cur) => (by(cur) < by(min) ? cur : min), items[0])
  }
}
export function isInteger(value) {
  return Math.floor(value) == value
}
export function flatten(items) {
  return [].concat.apply([], items)
}
